import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as R from "remeda";

import { StudyContextData, SubmissionContextData } from "@/context/AppContext";

import { Alert } from "@shared/components/Alert";
import { SecondaryButton } from "@shared/components/Button";

import { SubmissionForm } from "@/views/study/components/submission/SubmissionForm";

import { T } from "@/components/i18n";
import { StudyFileUploadHelp } from "@/components/help/StudyHelp";

import { Check, CircleAlert, TextSearch, XIcon } from "lucide-react";
import clsx from "clsx";

interface ClaudeManifestFlowProps {
  studyContext: StudyContextData;
  submissionContext: SubmissionContextData;
}

export function ClaudeManifestFlow({ submissionContext, studyContext }: ClaudeManifestFlowProps) {
  const { t } = useTranslation();
  const { study } = studyContext;
  const { resetValidation, validation } = submissionContext;

  const urls = validation?.status === "claude_manifest" ? validation.conversationsUrls : [];

  const [clicked, setClicked] = useState<Record<string, boolean>>({});

  if (!study || validation?.status !== "claude_manifest") {
    return null;
  }

  const anyClicked = urls.some((u) => clicked[u]);
  const allClicked = urls.every((u) => clicked[u]);

  return (
    <SubmissionForm submissionContext={submissionContext} studyContext={studyContext}>
      <div className="flex flex-col gap-y-4">
        <Alert
          variant="warning"
          status={t("view.submission.flow.claudeManifest.alert")}
          Icon={TextSearch}
        />
        <div
          className={clsx("border-b border-neutral-200 dark:border-neutral-800", {
            "pb-4": allClicked,
            "pb-6": !allClicked,
          })}
        >
          <p className="mb-4 pb-4 border-b border-neutral-200 dark:border-neutral-800">
            <T i18nKey="view.submission.flow.claudeManifest.instructions1" count={urls.length} />
          </p>
          <p className="text-sm mb-4 flex items-center gap-x-1 text-neutral-500 dark:text-neutral-500">
            <CircleAlert size={14} className="-mt-px" />
            <T i18nKey="view.submission.flow.claudeManifest.warning" />
          </p>
          <div className="flex flex-col gap-y-4">
            {R.map(urls, (url, idx) => {
              const page = urls.length > 1 ? ` (${idx + 1} of ${urls.length})` : "";
              const title = `Download conversations${page}`;

              return (
                <SecondaryButton
                  key={idx}
                  highlight
                  onClick={() => {
                    setClicked((m) => ({ ...m, [url]: true }));
                    window.open(url, "_blank");
                  }}
                  round
                  disabled={clicked[url]}
                  className="w-fit"
                  variant={clicked[url] ? "secondary" : "default"}
                >
                  {title}
                </SecondaryButton>
              );
            })}
          </div>
          {!anyClicked && (
            <button
              className="text-sm underline text-neutral-500 dark:text-neutral-500 flex mt-6"
              onClick={resetValidation}
            >
              Wrong export? Try another file
            </button>
          )}
        </div>
        {allClicked && (
          <div className="pb-6 border-b border-neutral-200 dark:border-neutral-800">
            <p className="mb-4">
              <T i18nKey="view.submission.flow.claudeManifest.instructions2" count={urls.length} />
            </p>
            {urls.length > 1 && (
              <div className="mb-4">
                <p className="mb-1">
                  <T i18nKey="view.submission.flow.claudeManifest.instructionsMultiHeader" />
                </p>
                <ul>
                  <li className="flex gap-x-1 items-center">
                    <Check size={16} strokeWidth={3} className="stroke-green-600" />
                    <T i18nKey="view.submission.flow.claudeManifest.instructionsMulti1" />
                  </li>
                  <li className="flex gap-x-1 items-center">
                    <XIcon size={16} strokeWidth={3} className="stroke-red-500" />
                    <T i18nKey="view.submission.flow.claudeManifest.instructionsMulti2" />
                  </li>
                </ul>
              </div>
            )}
            <SecondaryButton round highlight onClick={resetValidation} className="w-fit">
              Return to file selection
            </SecondaryButton>
          </div>
        )}
        <div className="mt-2">
          <StudyFileUploadHelp studyId={study.id} />
        </div>
      </div>
    </SubmissionForm>
  );
}
